import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Eye, RefreshCw, Trash2, Check, X, BookOpen } from "lucide-react";

const STATUS_FILTERS = ["all", "pending", "accepted", "rejected", "paid", "unpaid"];

function StatusBadge({ status }) {
  const map = {
    pending: "bg-mtk-bg text-zinc-300 border-mtk-border",
    accepted: "bg-mtk-gold/10 text-mtk-gold border-mtk-gold/40",
    rejected: "bg-red-950/30 text-red-300 border-red-900/40",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 border font-mono-mtk text-[10px] tracking-[0.2em] uppercase rounded-none ${map[status] || map.pending}`}
      data-testid={`status-badge-${status}`}
    >
      {status}
    </span>
  );
}

function PaidBadge({ paid, t }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 border font-mono-mtk text-[9px] tracking-[0.2em] uppercase rounded-none ${
        paid
          ? "bg-mtk-gold/10 text-mtk-gold border-mtk-gold/40"
          : "bg-mtk-bg text-zinc-500 border-mtk-border"
      }`}
      data-testid={`paid-badge-${paid ? "yes" : "no"}`}
    >
      {paid ? `€ ${t.admin.paidBadge}` : t.admin.unpaidBadge}
    </span>
  );
}

export default function AdminDashboard() {
  const { t, lang, toggle } = useLang();
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0, paid: 0, unpaid: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "paid") params.paid = true;
      else if (filter === "unpaid") params.paid = false;
      else if (filter !== "all") params.status = filter;
      const [appsRes, statsRes] = await Promise.all([
        api.get("/admin/applications", { params }),
        api.get("/admin/applications/stats"),
      ]);
      setApps(appsRes.data || []);
      setStats(statsRes.data || stats);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      toast.success(`→ ${status}`);
      if (selected && selected.id === id) setSelected({ ...selected, status });
      fetchData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/applications/${selected.id}`, { notes });
      setSelected(data);
      toast.success("✓");
      fetchData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("✓");
      if (selected && selected.id === id) setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openDetail = (app) => {
    setSelected(app);
    setNotes(app.notes || "");
  };

  return (
    <div className="min-h-screen bg-mtk-bg text-white flex flex-col" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="border-b border-mtk-border bg-mtk-bg/95 backdrop-blur-xl sticky top-0 z-30">
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-baseline gap-2" data-testid="dashboard-brand">
              <span className="font-heading text-lg font-black tracking-[0.2em] text-white">MTK</span>
              <span className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold uppercase">ADMIN</span>
            </Link>
            <span className="hidden md:inline-block text-zinc-700">/</span>
            <span className="hidden md:inline-block font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              {t.admin.dashboardTitle}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/blog"
              className="flex items-center gap-1.5 font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-mtk-gold transition-colors border border-white/10 hover:border-mtk-gold/40 px-3 py-2"
              data-testid="admin-blog-link"
            >
              <BookOpen className="w-3 h-3" />
              BLOG
            </Link>
            <span className="hidden sm:inline-block font-mono-mtk text-[10px] tracking-[0.2em] text-zinc-500">
              {admin?.email}
            </span>
            <button
              onClick={toggle}
              className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-mtk-gold transition-colors border border-white/10 hover:border-mtk-gold/40 px-3 py-2"
              data-testid="dashboard-language-toggle"
            >
              <span className={lang === "es" ? "text-mtk-gold" : ""}>ES</span>
              <span className="mx-1 text-white/20">/</span>
              <span className={lang === "en" ? "text-mtk-gold" : ""}>EN</span>
            </button>
            <button
              onClick={fetchData}
              data-testid="dashboard-refresh-btn"
              className="text-zinc-400 hover:text-mtk-gold p-2 border border-white/10 hover:border-mtk-gold/40 transition-colors"
              aria-label="refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              variant="outline"
              className="rounded-none border-mtk-border bg-transparent text-zinc-300 hover:bg-white/5 hover:text-mtk-gold font-mono-mtk text-[10px] tracking-[0.3em] uppercase h-10 px-4"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" />
              {t.admin.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-10 py-8 md:py-10 max-w-[1600px] mx-auto w-full">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-mtk-border border border-mtk-border mb-8">
          {[
            { label: t.admin.total, value: stats.total, accent: false },
            { label: t.admin.pending, value: stats.pending, accent: true },
            { label: t.admin.accepted, value: stats.accepted, accent: false },
            { label: t.admin.rejected, value: stats.rejected, accent: false },
            { label: t.admin.paid, value: stats.paid, accent: true, gold: true },
          ].map((s, i) => (
            <div key={i} className="bg-mtk-bg p-5 md:p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {s.label}
              </div>
              <div className={`mt-3 font-heading text-3xl md:text-4xl font-black tracking-tight ${s.gold ? "text-mtk-gold" : s.accent ? "text-mtk-gold" : "text-white"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`font-mono-mtk text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border transition-colors ${
                filter === f
                  ? "bg-mtk-gold text-mtk-bg border-mtk-gold"
                  : "bg-transparent text-zinc-400 border-mtk-border hover:border-mtk-gold/40 hover:text-white"
              }`}
            >
              {f === "all"
                ? t.admin.filterAll
                : f === "pending"
                ? t.admin.filterPending
                : f === "accepted"
                ? t.admin.filterAccepted
                : f === "rejected"
                ? t.admin.filterRejected
                : f === "paid"
                ? t.admin.filterPaid
                : t.admin.filterUnpaid}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border border-mtk-border bg-mtk-bg overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center font-mono-mtk text-xs tracking-[0.3em] text-mtk-gold" data-testid="apps-loading">
              LOADING...
            </div>
          ) : apps.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="apps-empty">{t.admin.noApps}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-mtk-border hover:bg-transparent">
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">Date</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">Name</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500 hidden md:table-cell">Email</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500 hidden lg:table-cell">Age</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500 hidden lg:table-cell">Time</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">Status</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">Pago</TableHead>
                  <TableHead className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id} className="border-mtk-border hover:bg-mtk-surface/40" data-testid={`app-row-${a.id}`}>
                    <TableCell className="font-mono-mtk text-[11px] text-zinc-500">
                      {new Date(a.created_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { day: "2-digit", month: "short" })}
                    </TableCell>
                    <TableCell className="text-white font-semibold">{a.name}</TableCell>
                    <TableCell className="text-zinc-400 text-sm hidden md:table-cell">{a.email}</TableCell>
                    <TableCell className="text-zinc-400 hidden lg:table-cell">{a.age}</TableCell>
                    <TableCell className="text-zinc-400 text-sm hidden lg:table-cell max-w-[180px] truncate">{a.weekly_time}</TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                    <TableCell><PaidBadge paid={!!a.paid} t={t} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openDetail(a)}
                          className="p-2 text-zinc-400 hover:text-mtk-gold border border-transparent hover:border-mtk-gold/30 transition-colors"
                          data-testid={`view-${a.id}`}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {a.status !== "accepted" && (
                          <button
                            onClick={() => updateStatus(a.id, "accepted")}
                            className="p-2 text-zinc-400 hover:text-mtk-gold border border-transparent hover:border-mtk-gold/30 transition-colors"
                            data-testid={`accept-${a.id}`}
                            title="Accept"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {a.status !== "rejected" && (
                          <button
                            onClick={() => updateStatus(a.id, "rejected")}
                            className="p-2 text-zinc-400 hover:text-red-400 border border-transparent hover:border-red-900/40 transition-colors"
                            data-testid={`reject-${a.id}`}
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => remove(a.id)}
                          className="p-2 text-zinc-500 hover:text-red-400 border border-transparent hover:border-red-900/40 transition-colors"
                          data-testid={`delete-${a.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto bg-mtk-surface border-mtk-border rounded-none text-white scrollbar-gold"
          data-testid="application-detail-dialog"
        >
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">APPLICATION</span>
                  <StatusBadge status={selected.status} />
                  <PaidBadge paid={!!selected.paid} t={t} />
                </div>
                <DialogTitle className="font-heading text-3xl font-black uppercase tracking-tight text-white">
                  {selected.name}
                </DialogTitle>
                <DialogDescription className="font-mono-mtk text-[11px] tracking-[0.2em] text-zinc-500">
                  {selected.email} · {selected.age} · {new Date(selected.created_at).toLocaleString(lang === "es" ? "es-ES" : "en-US")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {[
                  { label: t.form.labels.situation, value: selected.current_situation },
                  { label: t.form.labels.objective, value: selected.objective },
                  { label: t.form.labels.time, value: selected.weekly_time },
                  { label: t.form.labels.block, value: selected.what_holds_back },
                  { label: t.form.labels.why, value: selected.why_mtk },
                ].map((f, i) => (
                  <div key={i} className="border-l-2 border-mtk-gold/40 pl-4">
                    <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80 mb-1">
                      {f.label}
                    </div>
                    <p className="text-zinc-200 whitespace-pre-wrap">{f.value}</p>
                  </div>
                ))}

                <div className="pt-4 border-t border-mtk-border">
                  <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold/80 mb-2">
                    {t.admin.notes}
                  </div>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Notas internas…"
                    data-testid="admin-notes-input"
                    className="rounded-none bg-mtk-bg border-mtk-border text-white focus-visible:ring-1 focus-visible:ring-mtk-gold focus-visible:ring-offset-0"
                  />
                  <Button
                    onClick={saveNotes}
                    data-testid="admin-save-notes-btn"
                    variant="outline"
                    className="mt-3 rounded-none border-mtk-gold/40 text-mtk-gold bg-transparent hover:bg-mtk-gold/10 hover:text-mtk-gold-light font-mono-mtk text-[10px] tracking-[0.3em] uppercase h-10"
                  >
                    {t.admin.saveNotes}
                  </Button>
                </div>

                <div className="pt-4 border-t border-mtk-border flex flex-wrap gap-2">
                  <Button
                    onClick={() => updateStatus(selected.id, "accepted")}
                    data-testid="dialog-accept-btn"
                    className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-10"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    {t.admin.accept}
                  </Button>
                  <Button
                    onClick={() => updateStatus(selected.id, "rejected")}
                    data-testid="dialog-reject-btn"
                    variant="outline"
                    className="rounded-none border-red-900/50 bg-transparent text-red-300 hover:bg-red-950/40 font-bold uppercase tracking-[0.2em] text-xs h-10"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" />
                    {t.admin.reject}
                  </Button>
                  <Button
                    onClick={() => updateStatus(selected.id, "pending")}
                    data-testid="dialog-reset-btn"
                    variant="outline"
                    className="rounded-none border-mtk-border bg-transparent text-zinc-300 hover:bg-white/5 font-bold uppercase tracking-[0.2em] text-xs h-10"
                  >
                    {t.admin.reset}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
